// @ts-nocheck
// SpaceDetailScreen — per-space membership management for the INTERNAL/PUBLIC
// partition. Member list (grant/revoke + role), append-only history (a trust
// feature, never hidden), and the deactivate-never-delete lifecycle. Mutations
// are org-admin only (enforced server-side); non-admins see a read-only notice.
import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Pressable,
} from 'react-native';
import { Colors, FontWeight, Radii } from '../../theme/tokens';
import { useApp, navigate } from '../../store/AppStore';
import { useAuth } from '../../store/AuthContext';
import { useResponsive } from '../../theme/responsive';
import { ScreenShell } from '../../components/WebShell';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { spacesService } from '../../services/spaces/spaces.service';
import {
  ApiSpace, ApiSpaceMember, ApiMembershipEvent, SpaceRole,
  SPACE_ROLE_LABELS, requesterSourceLabel,
} from '../../schema/spaces/space.schema';

const EVENT_CFG: Record<string, { label: string; tone: string; icon: string }> = {
  CREATE:      { label: 'Created',     tone: 'neutral', icon: 'Plus' },
  GRANT:       { label: 'Granted',     tone: 'success', icon: 'UserCheck' },
  REVOKE:      { label: 'Revoked',     tone: 'danger',  icon: 'UserMinus' },
  ROLE_CHANGE: { label: 'Role change', tone: 'warn',    icon: 'Refresh' },
  DEACTIVATE:  { label: 'Deactivated', tone: 'neutral', icon: 'Lock' },
};
const TONE_COLOR: Record<string, { c: string; bg: string; line: string }> = {
  neutral: { c: Colors.neutral, bg: Colors.neutralSoft, line: Colors.neutralLine },
  success: { c: Colors.success, bg: Colors.successSoft, line: Colors.successLine },
  danger:  { c: Colors.danger,  bg: Colors.dangerSoft,  line: Colors.dangerLine },
  warn:    { c: Colors.warn,    bg: Colors.warnSoft,    line: Colors.warnLine },
};

export function SpaceDetailScreen({ id }: { id: string }) {
  const { spaceById, agentMap, deactivateSpace, grantMember, revokeMember } = useApp();
  const { isAdmin } = useAuth();
  const { pagePadX, isMobile } = useResponsive();

  const [space, setSpace] = useState<ApiSpace | undefined>(spaceById(id));
  const [members, setMembers] = useState<ApiSpaceMember[]>([]);
  const [events, setEvents] = useState<ApiMembershipEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiSpaceMember | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const nameOf = (uid: string) => agentMap?.[uid] || uid;

  const reload = useCallback(async () => {
    try {
      const [sp, mem, hist] = await Promise.allSettled([
        spacesService.get(id),
        spacesService.listMembers(id),
        spacesService.listHistory(id),
      ]);
      if (sp.status === 'fulfilled') setSpace(sp.value);
      else { setNotFound(true); }
      if (mem.status === 'fulfilled') setMembers((mem.value as any).data || []);
      // History is org-admin only — a 403 for non-admins just leaves it empty.
      if (hist.status === 'fulfilled') setEvents((hist.value as any).data || []);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  if (notFound && !space) {
    return (
      <ScreenShell crumbs={[{ label: 'Settings' }, { label: 'Spaces', to: '/admin/settings/spaces' }, { label: '—' }]}>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: Colors.ink3 }}>Space not found.</Text>
        </View>
      </ScreenShell>
    );
  }
  if (!space) {
    return (
      <ScreenShell crumbs={[{ label: 'Settings' }, { label: 'Spaces', to: '/admin/settings/spaces' }]}>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: Colors.ink3 }}>Loading…</Text>
        </View>
      </ScreenShell>
    );
  }

  const internal = space.type === 'INTERNAL';
  const readOnly = !space.isActive;
  const canManage = isAdmin && !readOnly;
  const memberIds = members.map(m => m.platformUserId);

  return (
    <ScreenShell crumbs={[{ label: 'Settings' }, { label: 'Spaces', to: '/admin/settings/spaces' }, { label: space.name }]}>
      <View style={S.root}>
        <View style={{ paddingHorizontal: pagePadX, paddingTop: 16 }}>
          <TouchableOpacity onPress={() => navigate('/admin/settings/spaces')} style={S.backLink}>
            <Icon name="ArrowLeft" size={13} color={Colors.ink2} />
            <Text style={S.backLinkText}>All spaces</Text>
          </TouchableOpacity>
        </View>

        <View style={[S.pageHead, { paddingHorizontal: pagePadX }]}>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Text style={S.pageTitle}>{space.name}</Text>
              {internal && (
                <View style={S.lockupLg}>
                  <Icon name="Lock" size={13} color="#fff" />
                  <Text style={S.lockupLgText}>INTERNAL</Text>
                  <Text style={S.lockupLgKey}>{(space.key || '').toUpperCase()}</Text>
                </View>
              )}
            </View>
            <Text style={S.pageSub}>
              {space.type} space · key <Text style={S.mono}>{(space.key || '').toUpperCase()}</Text> · requesters from <Text style={{ fontWeight: FontWeight.semibold, color: Colors.ink2 }}>{requesterSourceLabel(space.requesterSource)}</Text>
            </Text>
          </View>
          {canManage && (
            <PrimaryButton title="Grant access" size="md" variant="primary"
              leftIcon={<Icon name="UserCheck" size={14} color="#fff" />} onPress={() => setShowGrant(true)} />
          )}
        </View>

        <ScrollView style={S.body} contentContainerStyle={{ padding: isMobile ? 16 : 24, maxWidth: 1000, width: '100%', alignSelf: 'center' }}>
          {readOnly && (
            <View style={[S.alert, { backgroundColor: Colors.paper2, borderColor: Colors.line }]}>
              <Icon name="Lock" size={16} color={Colors.ink2} />
              <View style={{ flex: 1 }}>
                <Text style={S.alertTitle}>This space is deactivated</Text>
                <Text style={S.alertBody}>Membership is read-only. History is retained — spaces are never deleted.</Text>
              </View>
            </View>
          )}
          {!isAdmin && !readOnly && (
            <View style={[S.alert, { backgroundColor: Colors.infoSoft, borderColor: Colors.infoLine }]}>
              <Icon name="Eye" size={16} color={Colors.info} />
              <View style={{ flex: 1 }}>
                <Text style={[S.alertTitle, { color: Colors.info }]}>You're viewing membership read-only</Text>
                <Text style={S.alertBody}>Only a workspace admin can grant or revoke access in this space.</Text>
              </View>
            </View>
          )}

          {/* Members */}
          <View style={S.sectionH}>
            <Text style={S.sectionTitle}>Members · {members.length}</Text>
            <Text style={S.sectionSub}>Every member was granted explicitly. There is no manager / org-chart shortcut.</Text>
          </View>
          <View style={S.card}>
            <View style={[S.row, S.rowHead]}>
              <Text style={[S.th, { flex: 1 }]}>Member</Text>
              <Text style={[S.th, S.colRole]}>Role</Text>
              {!isMobile && <Text style={[S.th, S.colGranted]}>Granted by</Text>}
              {!isMobile && <Text style={[S.th, S.colGranted]}>Granted on</Text>}
              <View style={S.colAction} />
            </View>
            {members.map((m, i) => {
              const name = nameOf(m.platformUserId);
              const initials = name.split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
              const grantor = m.grantedBy && agentMap?.[m.grantedBy] ? agentMap[m.grantedBy] : 'System';
              return (
                <View key={m.platformUserId} style={[S.row, i < members.length - 1 && S.rowBorder]}>
                  <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <View style={[S.avatar, { backgroundColor: Colors.internalSoft }]}><Text style={[S.avatarText, { color: Colors.internalInk }]}>{initials}</Text></View>
                    <View style={{ flex: 1 }}>
                      <Text style={S.memberName} numberOfLines={1}>{name}</Text>
                      <Text style={S.memberId} numberOfLines={1}>{m.platformUserId}</Text>
                    </View>
                  </View>
                  <View style={S.colRole}>
                    <RoleControl member={m} canManage={canManage && m.role !== 'VIEWER'}
                      onChange={async (role) => { await grantMember(space.id, m.platformUserId, role); reload(); }} />
                  </View>
                  {!isMobile && <Text style={[S.cellDim, S.colGranted]} numberOfLines={1}>{grantor}</Text>}
                  {!isMobile && <Text style={[S.cellDim, S.colGranted]}>{m.grantedAt ? new Date(m.grantedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}</Text>}
                  <View style={S.colAction}>
                    <TouchableOpacity disabled={!canManage} onPress={() => setRevokeTarget(m)}
                      style={[S.iconBtn, !canManage && { opacity: 0.35 }]}>
                      <Icon name="UserMinus" size={14} color={Colors.ink2} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
            {members.length === 0 && (
              <View style={[S.row, { justifyContent: 'center' }]}>
                <Text style={{ color: Colors.ink3, padding: 16 }}>{loading ? 'Loading members…' : 'No members yet.'}</Text>
              </View>
            )}
          </View>

          {/* Append-only history */}
          <View style={[S.sectionH, { marginTop: 24 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
              <Icon name="History" size={15} color={Colors.ink3} />
              <Text style={S.sectionTitle}>Membership history</Text>
            </View>
            <Text style={S.sectionSub}>Append-only. Every grant, revoke and role change is recorded and never edited.</Text>
          </View>
          <View style={S.card}>
            {events.length === 0 ? (
              <View style={{ padding: 16 }}><Text style={{ color: Colors.ink3 }}>{loading ? 'Loading history…' : (isAdmin ? 'No events yet.' : 'History is available to workspace admins.')}</Text></View>
            ) : (
              [...events].reverse().map((e, i) => {
                const cfg = EVENT_CFG[e.eventType] || EVENT_CFG.GRANT;
                const tone = TONE_COLOR[cfg.tone] || TONE_COLOR.neutral;
                const subj = e.platformUserId ? nameOf(e.platformUserId) : null;
                const actor = e.actorId && agentMap?.[e.actorId] ? agentMap[e.actorId] : 'System';
                return (
                  <View key={e.id} style={[S.histRow, i < events.length - 1 && S.rowBorder]}>
                    <View style={[S.histIcon, { backgroundColor: tone.bg, borderColor: tone.line }]}>
                      <Icon name={cfg.icon as any} size={13} color={tone.c} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <View style={[S.chip, { backgroundColor: tone.bg, borderColor: tone.line }]}>
                          <Text style={[S.chipText, { color: tone.c }]}>{cfg.label}</Text>
                        </View>
                        {subj ? <Text style={{ fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink }}>{subj}</Text> : null}
                        {e.role ? <Text style={{ fontSize: 13, color: Colors.ink3 }}>· {SPACE_ROLE_LABELS[e.role as SpaceRole]}</Text> : null}
                      </View>
                      <Text style={{ fontSize: 11.5, color: Colors.ink4, marginTop: 2 }}>by {actor} · {e.at ? new Date(e.at).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</Text>
                    </View>
                  </View>
                );
              })
            )}
          </View>

          {/* Lifecycle — deactivate, never delete. Hidden for the PUBLIC default. */}
          {canManage && internal && (
            <>
              <View style={[S.sectionH, { marginTop: 24 }]}>
                <Text style={S.sectionTitle}>Lifecycle</Text>
                <Text style={S.sectionSub}>Spaces are deactivated, never deleted — history and tickets are retained for audit.</Text>
              </View>
              <View style={[S.card, { borderColor: Colors.dangerLine, padding: 16, flexDirection: 'row', alignItems: 'center', gap: 16 }]}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.danger }}>Deactivate this space</Text>
                  <Text style={{ fontSize: 12.5, color: Colors.ink3, marginTop: 2 }}>New tickets can no longer be created. Existing tickets and the full membership history stay accessible to members, read-only.</Text>
                </View>
                <PrimaryButton title="Deactivate" size="md" variant="danger"
                  leftIcon={<Icon name="Lock" size={13} color="#fff" />} onPress={() => setConfirmDeactivate(true)} />
              </View>
            </>
          )}
        </ScrollView>
      </View>

      {showGrant && (
        <GrantModal space={space} memberIds={memberIds} agentMap={agentMap}
          onCancel={() => setShowGrant(false)}
          onGrant={async (uid, role) => { const ok = await grantMember(space.id, uid, role); if (ok) { setShowGrant(false); reload(); } }} />
      )}

      {revokeTarget && (
        <ConfirmModal
          tone="danger" icon="UserMinus" title="Revoke access?"
          body={<Text style={S.confirmBody}>Revoking <Text style={S.confirmStrong}>{nameOf(revokeTarget.platformUserId)}</Text> removes their access to <Text style={S.confirmStrong}>every ticket, message and requester</Text> in <Text style={S.confirmStrong}>{space.name}</Text> immediately. Any open session is bounced to an accessible space. This is recorded in the membership history.</Text>}
          confirmLabel="Revoke access"
          onCancel={() => setRevokeTarget(null)}
          onConfirm={async () => { const t = revokeTarget; setRevokeTarget(null); await revokeMember(space.id, t.platformUserId); reload(); }} />
      )}

      {confirmDeactivate && (
        <ConfirmModal
          tone="danger" icon="Lock" title="Deactivate space?"
          body={<Text style={S.confirmBody}>No new tickets can be created in <Text style={S.confirmStrong}>{space.name}</Text>. Existing tickets and membership history are retained and become read-only. The space is never deleted.</Text>}
          confirmLabel="Deactivate space"
          onCancel={() => setConfirmDeactivate(false)}
          onConfirm={async () => { setConfirmDeactivate(false); const ok = await deactivateSpace(space.id); if (ok) navigate('/admin/settings/spaces'); }} />
      )}
    </ScreenShell>
  );
}

// Role: an inline select on web for admins; a static chip otherwise.
function RoleControl({ member, canManage, onChange }: { member: ApiSpaceMember; canManage: boolean; onChange: (r: SpaceRole) => void }) {
  if (canManage && Platform.OS === 'web') {
    return (
      // @ts-ignore — DOM select on web; react-native-web passes it through.
      <select value={member.role} onChange={(e: any) => onChange(e.target.value)}
        style={{ height: 28, fontSize: 12.5, padding: '0 8px', borderRadius: 5, border: `1px solid ${Colors.line}`, background: Colors.card, color: Colors.ink }}>
        <option value="ADMIN">Admin</option>
        <option value="AGENT">Agent</option>
      </select>
    );
  }
  return (
    <View style={[S.chip, { backgroundColor: Colors.internalSoft, borderColor: Colors.internalLine, alignSelf: 'flex-start' }]}>
      <View style={[S.chipDot, { backgroundColor: Colors.internal }]} />
      <Text style={[S.chipText, { color: Colors.internalInk }]}>{SPACE_ROLE_LABELS[member.role]}</Text>
    </View>
  );
}

// Grant modal — pick an explicit platform user → role. No org-chart import.
function GrantModal({ space, memberIds, agentMap, onCancel, onGrant }) {
  const candidates = Object.keys(agentMap || {})
    .filter((uid) => !memberIds.includes(uid))
    .map((uid) => ({ id: uid, name: agentMap[uid] }));
  const [uid, setUid] = useState(candidates[0]?.id || '');
  const [role, setRole] = useState<SpaceRole>('AGENT');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!uid || submitting) return;
    setSubmitting(true);
    await onGrant(uid, role);
    setSubmitting(false);
  };

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modal, Platform.OS === 'web' ? { width: 520 } as any : { width: '96%' }]}>
        <View style={S.modalHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={S.modalHeadIcon}><Icon name="UserCheck" size={16} color={Colors.internal} /></View>
            <View>
              <Text style={S.modalTitle}>Grant access</Text>
              <Text style={S.modalSub}>to {space.name}</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={15} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <View style={{ padding: 22, gap: 16 }}>
          <View>
            <Text style={S.label}>Platform user</Text>
            {candidates.length === 0 ? (
              <Text style={{ fontSize: 12.5, color: Colors.ink3, paddingVertical: 8 }}>Everyone available is already a member.</Text>
            ) : Platform.OS === 'web' ? (
              // @ts-ignore
              <select value={uid} onChange={(e: any) => setUid(e.target.value)}
                style={{ height: 34, fontSize: 13, padding: '0 10px', borderRadius: 5, border: `1px solid ${Colors.line}`, background: Colors.card, color: Colors.ink, width: '100%' }}>
                {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : null}
            <Text style={[S.hintText, { marginTop: 4 }]}>Pick an explicit user. There is no "add by manager" or org-chart import — membership is always direct.</Text>
          </View>
          <View>
            <Text style={S.label}>Role</Text>
            {/* @ts-ignore */}
            <select value={role} onChange={(e: any) => setRole(e.target.value)}
              style={{ height: 34, fontSize: 13, padding: '0 10px', borderRadius: 5, border: `1px solid ${Colors.line}`, background: Colors.card, color: Colors.ink, width: '100%' }}>
              <option value="ADMIN">Admin — full access + can manage membership</option>
              <option value="AGENT">Agent — work tickets in this space</option>
            </select>
          </View>
        </View>
        <View style={S.modalFoot}>
          <PrimaryButton title="Cancel" size="md" variant="default" onPress={onCancel} />
          <PrimaryButton title={`Grant ${role === 'ADMIN' ? 'Admin' : 'Agent'}`} size="md" variant="primary"
            leftIcon={<Icon name="UserCheck" size={13} color="#fff" />} onPress={submit} disabled={!uid || submitting} />
        </View>
      </View>
    </View>
  );
}

// Small confirm modal — names the consequence (revoke / deactivate).
function ConfirmModal({ tone = 'danger', icon, title, body, confirmLabel, onCancel, onConfirm }) {
  const toneC = TONE_COLOR[tone] || TONE_COLOR.danger;
  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modal, Platform.OS === 'web' ? { width: 480 } as any : { width: '92%' }]}>
        <View style={S.modalHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={[S.modalHeadIcon, { backgroundColor: toneC.bg }]}><Icon name={icon} size={15} color={toneC.c} /></View>
            <Text style={S.modalTitle}>{title}</Text>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={14} color={Colors.ink2} /></TouchableOpacity>
        </View>
        <View style={{ padding: 22 }}>{body}</View>
        <View style={S.modalFoot}>
          <PrimaryButton title="Cancel" size="md" variant="default" onPress={onCancel} />
          <PrimaryButton title={confirmLabel} size="md" variant={tone === 'danger' ? 'danger' : 'primary'} onPress={onConfirm} />
        </View>
      </View>
    </View>
  );
}

const S = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  backLink: { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', paddingVertical: 4 },
  backLinkText: { fontSize: 12.5, color: Colors.ink2, fontWeight: FontWeight.medium },
  pageHead: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, paddingTop: 8, paddingBottom: 20, backgroundColor: Colors.card, borderBottomWidth: 1, borderBottomColor: Colors.line, flexWrap: 'wrap' },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 6 },
  mono: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
  body: { flex: 1 },

  lockupLg: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 28, paddingHorizontal: 12, borderRadius: Radii.full, backgroundColor: Colors.internal },
  lockupLgText: { fontSize: 12, fontWeight: FontWeight.semibold, color: '#fff', letterSpacing: 0.3 },
  lockupLgKey: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, fontSize: 12, fontWeight: FontWeight.bold, color: '#fff', opacity: 0.85 },

  alert: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 14, borderWidth: 1, borderRadius: Radii.r3, marginBottom: 16 },
  alertTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink },
  alertBody: { fontSize: 12.5, color: Colors.ink3, marginTop: 2, lineHeight: 18 },

  sectionH: { marginBottom: 10 },
  sectionTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink },
  sectionSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },

  card: { backgroundColor: Colors.card, borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r3, overflow: 'hidden' },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 14, gap: 10 },
  rowHead: { backgroundColor: Colors.paper2, paddingVertical: 9 },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  th: { fontSize: 11, fontWeight: FontWeight.medium, color: Colors.ink3, letterSpacing: 0.4, textTransform: 'uppercase' },
  colRole: { width: 150 },
  colGranted: { width: 140 },
  colAction: { width: 60, alignItems: 'flex-end' },

  avatar: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: 10, fontWeight: FontWeight.bold },
  memberName: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink },
  memberId: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, fontSize: 11, color: Colors.ink3, marginTop: 1 },
  cellDim: { fontSize: 12, color: Colors.ink3 },

  chip: { flexDirection: 'row', alignItems: 'center', gap: 5, height: 20, paddingHorizontal: 8, borderRadius: Radii.full, borderWidth: 1 },
  chipDot: { width: 6, height: 6, borderRadius: 3 },
  chipText: { fontSize: 10.5, fontWeight: FontWeight.semibold },

  histRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, paddingVertical: 10, paddingHorizontal: 14 },
  histIcon: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },

  iconBtn: { width: 28, height: 28, borderRadius: Radii.r2, alignItems: 'center', justifyContent: 'center' },

  // Modal
  backdrop: { ...StyleSheet.absoluteFillObject, position: 'fixed' as any, backgroundColor: 'rgba(16,24,40,0.42)', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 110 },
  modal: { backgroundColor: Colors.card, borderRadius: Radii.r4, overflow: 'hidden', maxWidth: '100%', ...(Platform.OS === 'web' ? { boxShadow: '0 16px 48px rgba(16,24,40,0.24)' } as any : {}) },
  modalHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 22, borderBottomWidth: 1, borderBottomColor: Colors.line },
  modalHeadIcon: { width: 32, height: 32, borderRadius: Radii.r2, backgroundColor: Colors.internalSoft, alignItems: 'center', justifyContent: 'center' },
  modalTitle: { fontSize: 18, fontWeight: FontWeight.semibold, color: Colors.ink },
  modalSub: { fontSize: 11.5, color: Colors.ink3, marginTop: 2 },
  modalFoot: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, padding: 22, paddingVertical: 14, borderTopWidth: 1, borderTopColor: Colors.line, backgroundColor: Colors.paper2 },
  label: { fontSize: 12, fontWeight: FontWeight.medium, color: Colors.ink2, marginBottom: 4 },
  hintText: { fontSize: 11, color: Colors.ink3, lineHeight: 15 },
  confirmBody: { fontSize: 13.5, color: Colors.ink2, lineHeight: 21 },
  confirmStrong: { fontWeight: FontWeight.semibold, color: Colors.ink },
});
